import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import { useFormik } from 'formik'
import { useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { CREATE_COURSE, UPDATE_COURSE } from '../../../gql/mutations/academyMutations'
import { SEARCH_RESOURCES_CENTER } from '../../../gql/queries/resourceCenterQuery'
import { GET_RESOURCE_CATEGORY_TREE } from '../../../gql/queries/resourceCategoryQuery'
import {
  ICourse,
  ICreateCourseResponse,
  IUpdateCourseResponse,
} from '../../../types/academy'
import { IResourceCenter } from '../../../types/resourceCenter'
import { IResourceCategoryTreeResponse } from '../../../types/resourceCategory'
import { parseId } from '../../../helpers'
import { flattenCategoryTree } from '../../resourceCenter/categoryTreeHelpers'
import { IntlShape, useIntl } from 'react-intl'
import {
  ACADEMY_MESSAGES as A,
  BACKEND_ERROR,
  REQUIRED_MARK,
} from '../academyMessages'

/** Fabrica: el mensaje necesita `intl`, asi que el esquema no puede ser un const. */
const buildValidationSchema = (intl: IntlShape) =>
  Yup.object().shape({
    name: Yup.string().required(intl.formatMessage(A.vNameRequired)),
  })

interface ISearchResourcesCenterResponse {
  resourcesCenterAdvancedSearch: IResourceCenter[]
}

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  // Si viene course, es edición; si no, creación
  course?: ICourse | null
}

export const ModalCourseForm: React.FC<IModalProps> = ({ isOpen, onClose, refetch, course }) => {
  const intl = useIntl()
  const isEdit = Boolean(course)

  const [createCourse, { loading: creating }] = useMutation<ICreateCourseResponse>(CREATE_COURSE)
  const [updateCourse, { loading: updating }] = useMutation<IUpdateCourseResponse>(UPDATE_COURSE)
  const loading = creating || updating

  // Imágenes del Resource Center para usar como portada
  const { data: dataResources } = useQuery<ISearchResourcesCenterResponse>(
    SEARCH_RESOURCES_CENTER,
    {
      variables: { resourceType: 'image', isActive: true, isCampaignMedia: false },
      fetchPolicy: 'cache-first',
    }
  )

  const { data: dataCategories } = useQuery<IResourceCategoryTreeResponse>(
    GET_RESOURCE_CATEGORY_TREE,
    { fetchPolicy: 'cache-first' }
  )

  const formik = useFormik({
    initialValues: {
      name: course?.name || '',
      description: course?.description || '',
      coverImageId: course?.coverImage?.id || '',
      categoryId: course?.category?.id || '',
      isActive: course?.isActive ?? true,
      order: course?.order ?? 0,
    },
    enableReinitialize: true,
    validationSchema: buildValidationSchema(intl),
    onSubmit: async (v) => {
      const courseData = {
        name: v.name,
        description: v.description || undefined,
        coverImageId: v.coverImageId ? parseId(v.coverImageId) : null,
        categoryId: v.categoryId ? parseId(v.categoryId) : null,
        isActive: v.isActive,
        order: v.order,
      }

      try {
        if (isEdit && course) {
          const result = await updateCourse({
            variables: { id: parseId(course.id), courseData },
          })
          if (result.data?.updateCourse.success) {
            toast.success(intl.formatMessage(A.toastCourseUpdated))
            onClose()
            refetch()
          } else if (result.data?.updateCourse.errors?.length) {
            toast.error(intl.formatMessage(BACKEND_ERROR, {message: result.data.updateCourse.errors[0].message}))
          }
        } else {
          const result = await createCourse({ variables: { courseData } })
          if (result.data?.createCourse.success) {
            toast.success(intl.formatMessage(A.toastCourseCreated))
            onClose()
            refetch()
            formik.resetForm()
          } else if (result.data?.createCourse.errors?.length) {
            toast.error(intl.formatMessage(BACKEND_ERROR, {message: result.data.createCourse.errors[0].message}))
          }
        }
      } catch (err) {
        console.error('Error saving course:', err)
        toast.error(intl.formatMessage(A.errSaveCourse))
      }
    },
  })

  useEffect(() => {
    if (!isOpen) formik.resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const imageOptions =
    dataResources?.resourcesCenterAdvancedSearch.map((resource) => ({
      label: resource.name,
      value: resource.id,
    })) || []

  const categoryOptions = flattenCategoryTree(dataCategories?.resourceCategoryTree || [])

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className={`bi ${isEdit ? 'bi-pencil-square' : 'bi-plus-circle'}`} />
          {intl.formatMessage(isEdit ? A.formEditCourse : A.formNewCourse)}
          {loading && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  {intl.formatMessage(A.name)}
                  <span className="text-danger ms-1">{REQUIRED_MARK}</span>
                </Form.Label>
                <Form.Control
                  name="name"
                  placeholder={intl.formatMessage(A.coursePlaceholder)}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={Boolean(formik.errors.name && formik.touched.name)}
                />
                {formik.errors.name && formik.touched.name && (
                  <Form.Text className="text-danger">{formik.errors.name}</Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(A.displayOrder)}</Form.Label>
                <Form.Control
                  type="number"
                  name="order"
                  value={formik.values.order}
                  onChange={formik.handleChange}
                  min="0"
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(A.description)}</Form.Label>
                <Form.Control
                  name="description"
                  onChange={formik.handleChange}
                  value={formik.values.description}
                  as="textarea"
                  rows={3}
                  placeholder={intl.formatMessage(A.descriptionPlaceholder)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(A.coverImage)}</Form.Label>
                <Select
                  isClearable
                  value={imageOptions.find((opt) => opt.value === formik.values.coverImageId) || null}
                  onChange={(option) => formik.setFieldValue('coverImageId', option?.value || '')}
                  options={imageOptions}
                  placeholder={intl.formatMessage(A.coverPlaceholder)}
                />
                <Form.Text className="text-muted">
                  {intl.formatMessage(A.coverHint)}
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(A.folder)}</Form.Label>
                <Select
                  isClearable
                  value={categoryOptions.find((opt) => opt.value === formik.values.categoryId) || null}
                  onChange={(option) => formik.setFieldValue('categoryId', option?.value || '')}
                  options={categoryOptions}
                  placeholder={intl.formatMessage(A.noFolder)}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label={intl.formatMessage(A.published)}
                  checked={formik.values.isActive}
                  onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          {intl.formatMessage(A.cancel)}
        </Button>
        <Button variant="primary" onClick={() => formik.handleSubmit()} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {intl.formatMessage(A.saving)}
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1" />
              {intl.formatMessage(isEdit ? A.saveChanges : A.createCourse)}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
